import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { usePermission } from '@/entities/Permission';
import { useAuthStore } from '@/entities/Session';
import type { CaslAction, Subjects } from '@/shared/lib/casl';
import { checkPermissions, type PermissionMatchMode } from '@/shared/lib';

export interface RoutePermissionMeta {
  action: CaslAction;
  subject: Subjects;
}

export function permissionGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  if (!to.meta.permissions) {
    return next();
  }

  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    return next({ name: 'Login', query: { redirect: to.fullPath } });
  }

  const { can, isLoaded } = usePermission();

  const permissions = to.meta.permissions as RoutePermissionMeta[];
  const matchMode = to.meta.matchMode as PermissionMatchMode | undefined;
  // Sem regras carregadas nao ha o que autorizar. Isso e uma negativa de permissao, e nao uma
  // sessao invalida: mandar para Login desloga na pratica quem esta autenticado, e o proprio
  // beforeEach devolve esse usuario para Home, num vaivem que perde a rota pedida.
  const hasPermission =
    isLoaded.value &&
    checkPermissions(
      permissions,
      ({ action, subject }) => can(action, subject).value,
      matchMode,
    );

  if (hasPermission) {
    return next();
  }

  // AC40: uma URL sem permissao digitada de dentro do app nao navega e nao redireciona — a tela
  // atual continua onde esta. Numa aba nova nao ha tela atual para manter, e so ai vai para Home.
  if (from.name) {
    return next(false);
  }

  return next({ name: 'Home' });
}
